// The Dashboards list while it loads (dashboard-assistant-polish-4 requirement 19, walk item 34):
// the shape of the mode it will open in, rows in the table's card shell or cards in board lanes,
// so the page does not show cards and then turn into a table. Kept free of the page's imports, so
// the app's cold-load skeleton can draw it too.

import React from "react";

/** How many row or card shapes the skeleton draws: enough to fill a first screen. */
const ROWS = 8;
const LANES = 3;
const CARDS_PER_LANE = 3;

const bar = (width: string, height = 10): React.CSSProperties => ({
  width,
  height,
  borderRadius: 4,
  background: "var(--fp-border)",
});

export type DashboardsListMode = "table" | "board";

/** Where the list keeps the viewer's mode, read by the page and by the cold-load skeleton. */
export const DASHBOARDS_VIEW_MODE_KEY = "rms-view-mode:dashboards";

/** The mode the list opens in: the viewer's stored choice, the table when there is none. */
export const storedDashboardsMode = (): DashboardsListMode => {
  try {
    return localStorage.getItem(DASHBOARDS_VIEW_MODE_KEY) === "board"
      ? "board"
      : "table";
  } catch {
    return "table";
  }
};

export const DashboardsSkeleton: React.FC<{ mode: DashboardsListMode }> = ({
  mode,
}) =>
  mode === "board" ? (
    <div
      aria-busy="true"
      data-dashboards-skeleton="board"
      className="flex flex-1 min-h-0 gap-3 overflow-hidden animate-pulse"
    >
      {Array.from({ length: LANES }, (_, lane) => (
        <div
          key={lane}
          className="flex flex-col gap-2 flex-none"
          style={{ width: 270 }}
        >
          <div style={{ ...bar("40%", 9), margin: "6px 0 8px" }} />
          {Array.from({ length: CARDS_PER_LANE }, (_, card) => (
            <div
              key={card}
              data-skeleton-card=""
              style={{
                background: "var(--fp-surface)",
                border: "1px solid var(--fp-border)",
                borderRadius: 10,
                padding: "12px",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <div style={bar("65%", 12)} />
              <div style={bar("40%")} />
            </div>
          ))}
        </div>
      ))}
    </div>
  ) : (
    <div
      aria-busy="true"
      data-dashboards-skeleton="table"
      className="flex flex-1 min-h-0 flex-col overflow-hidden animate-pulse"
      style={{
        background: "var(--fp-surface)",
        border: "1px solid var(--fp-border)",
        borderRadius: "var(--fp-card-r)",
        boxShadow: "var(--fp-lift)",
      }}
    >
      <div
        style={{
          height: 36,
          borderBottom: "1px solid var(--fp-border)",
          display: "flex",
          alignItems: "center",
          padding: "0 12px",
        }}
      >
        <div style={bar("12%", 8)} />
      </div>
      {Array.from({ length: ROWS }, (_, row) => (
        <div
          key={row}
          data-skeleton-row=""
          style={{
            height: "var(--fp-row-h)",
            minHeight: 40,
            borderBottom: "1px solid var(--fp-border)",
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "0 12px",
          }}
        >
          <div style={bar("32%", 12)} />
          <div style={{ flex: 1 }} />
          <div style={bar("8%")} />
          <div style={bar("10%")} />
        </div>
      ))}
    </div>
  );
